import { Stack } from '@mui/material';
import { FC } from 'react';
import {
  BooleanField,
  NumberField,
  TextField,
  useRecordContext,
  useTranslate,
  WithRecord,
} from 'react-admin';

import { GetBoExternalEmployeesOneResponse } from '@Common/types/bo_external/employees';

import { ShowFields, ShowSection, ShowWithNavigation } from '@sh4res/bo-common/components';
import { B2bEmployeeSendInviteButton } from '@sh4res/bo-common/components/B2bEmployee/B2bEmployeeSendInviteButton';
import {
  accountStatusChoices,
  genderChoices,
  taxResidenceCodeChoices,
} from '@sh4res/bo-common/components/B2bEmployee/choices';
import {
  ContractsField,
  EmailStateChip,
  IdField,
  SelectChipField,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { formatFullname } from '@sh4res/bo-common/utils';

import {
  leavingReasonChoices,
  socialStatusChoices,
  statusChoices,
} from '@Resources/employees/choices';
import { EmployeesEdit } from '@Resources/employees/EmployeesEdit';

export const EmployeesShow: FC = () => {
  const testId = 'BoExternalEmployeesShow';
  const record = useRecordContext<GetBoExternalEmployeesOneResponse>();
  const translate = useTranslate();

  return (
    <ShowWithNavigation
      id={record?.id}
      actions={false}
      titleActions={<B2bEmployeeSendInviteButton testId={`${testId}_SendInviteButton`} />}
      title={
        <WithRecord
          render={(record: GetBoExternalEmployeesOneResponse) => formatFullname(record)}
        />
      }
      subtitle={({ id }) => translate('resources.employees.subtitle', { id })}
      avatar={{ name_source: 'firstname' }}
      button={<EmployeesEdit resource="employees" />}>
      <ShowSection label="resources.employees.tabs.details.sections.details">
        <ShowFields<GetBoExternalEmployeesOneResponse>
          fields={{
            id: <IdField reference="employees" data-testid={`${testId}_IdField`} />,
            email: (
              <Stack direction="row" spacing={2} alignItems={'center'}>
                <TextField source="email" emptyText="-" data-testid={`${testId}_EmailField`} />
                <EmailStateChip
                  source="email_state"
                  emailSource="email"
                  logResource="b2b_employee"
                />
              </Stack>
            ),
            status: (
              <SelectChipField choices={statusChoices} data-testid={`${testId}_StatusField`} />
            ),
            user_id: <NumberField data-testid={`${testId}_UserIdField`} />,
            created_at: <UTCDateField data-testid={`${testId}_CreatedAtField`} />,
            updated_at: <UTCDateField data-testid={`${testId}_UpdatedAtField`} />,
          }}
        />
      </ShowSection>
      <ShowSection label="resources.employees.tabs.details.sections.account">
        <ShowFields<GetBoExternalEmployeesOneResponse>
          fields={{
            account_status: (
              <SelectChipField
                choices={accountStatusChoices}
                data-testid={`${testId}_AccountStatusField`}
              />
            ),
            is_activated_by_company: (
              <BooleanField data-testid={`${testId}_IsActivatedByCompanyField`} />
            ),
            activation_code_is_used: (
              <BooleanField data-testid={`${testId}_ActivationCodeIsUsedField`} />
            ),
            personal_details_confirmed: (
              <BooleanField data-testid={`${testId}_PersonalDetailsConfirmedField`} />
            ),
          }}
        />
      </ShowSection>
      <ShowSection label="resources.employees.tabs.details.sections.personal">
        <ShowFields<GetBoExternalEmployeesOneResponse>
          fields={{
            firstname: <TextField data-testid={`${testId}_FirstnameField`} />,
            lastname: <TextField data-testid={`${testId}_LastnameField`} />,
            gender: (
              <SelectChipField choices={genderChoices} data-testid={`${testId}_GenderField`} />
            ),
            name_in_use: <TextField data-testid={`${testId}_NameInUseField`} />,
            phone_number: <TextField data-testid={`${testId}_PhoneNumberField`} />,
            secondary_email: (
              <Stack direction="row" spacing={2} alignItems={'center'}>
                <TextField
                  source="secondary_email"
                  emptyText="-"
                  data-testid={`${testId}_SecondaryEmailField`}
                />
                <EmailStateChip
                  source="secondary_email_state"
                  emailSource="secondary_email"
                  logResource="b2b_employee"
                />
              </Stack>
            ),
            street: <TextField data-testid={`${testId}_StreetField`} />,
            postalcode: <TextField data-testid={`${testId}_PostalCodeField`} />,
            city: <TextField data-testid={`${testId}_CityField`} />,
            country: <TextField data-testid={`${testId}_CountryField`} />,
            language: <TextField data-testid={`${testId}_LanguageField`} />,
            place_of_birth: <TextField data-testid={`${testId}_PlaceOfBirthField`} />,
            country_of_birth: <TextField data-testid={`${testId}_CountryOfBirthField`} />,
            date_of_birth: <UTCDateField data-testid={`${testId}_DateOfBirthField`} />,
            postcode_of_birth: <TextField data-testid={`${testId}_PostcodeOfBirthField`} />,
            nationality: <TextField data-testid={`${testId}_NationalityField`} />,
            registration_number: <TextField data-testid={`${testId}_RegistrationNumberField`} />,
            nir: <TextField data-testid={`${testId}_NirField`} />,
            ntt: <TextField data-testid={`${testId}_NttField`} />,
            tax_number: <TextField data-testid={`${testId}_TaxNumberField`} />,
            retirement_date: <UTCDateField data-testid={`${testId}_RetirementDateField`} />,
            is_deceased: <BooleanField data-testid={`${testId}_IsDeceasedField`} />,
            date_of_decease: <UTCDateField data-testid={`${testId}_DateOfDeceaseField`} />,
          }}
        />
      </ShowSection>
      <ShowSection label="resources.employees.tabs.details.sections.work">
        <ShowFields<GetBoExternalEmployeesOneResponse>
          fields={{
            work_starting_date: <UTCDateField data-testid={`${testId}_WorkStartingDateField`} />,
            work_ending_date: <UTCDateField data-testid={`${testId}_WorkEndingDateField`} />,
            leaving_reason: (
              <SelectChipField
                choices={leavingReasonChoices}
                data-testid={`${testId}_LeavingReasonField`}
              />
            ),
            contract_ids: (
              <ContractsField
                source="contract_ids"
                label="resources.bo_common.b2b.employees.fields.contract_ids"
                data-testid={`${testId}_ContractIdsField`}
              />
            ),
            social_status: (
              <SelectChipField
                choices={socialStatusChoices}
                data-testid={`${testId}_SocialStatusField`}
              />
            ),
            role_in_company: <TextField data-testid={`${testId}_RoleInCompanyField`} />,
            csp_code: <TextField data-testid={`${testId}_CspCodeField`} />,
          }}
        />
      </ShowSection>
      <ShowSection label="resources.employees.tabs.details.sections.financial">
        <ShowFields<GetBoExternalEmployeesOneResponse>
          fields={{
            tax_residence_code: (
              <SelectChipField
                source=""
                choices={taxResidenceCodeChoices}
                data-testid={`${testId}_TaxResidenceCodeField`}
              />
            ),
          }}
        />
      </ShowSection>
    </ShowWithNavigation>
  );
};
